import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Platform, View} from 'react-native';
import {useStyles} from '../../../../shared/hooks/styles';
import {DeviceSize} from '../../../../shared/theme/helpers';
import {Text} from '../../../../shared/components/text';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import {useSelector} from 'react-redux';
import {esiaUserHasNoCardsSelector} from '../../../../redux/selectors/login';
import {IssuePointsMapState} from '../../esiaRegCard/components/map/useMap';
import {initialYandexZoomValue} from '../../esiaRegCard/components/map/models';
import {useTheme} from '../../../../shared/theme';
import {images} from '../../../../assets/icons';
import {Link} from '../../../../shared/components/link';
import {formatPhone, formatPhoneToCallable} from '../../../../shared/pipes';
import {Clusterer, Placemark, YMaps, Map} from 'react-yandex-maps';
import MapPin from '../../../../assets/icons/map-pin.svg';
import {GetIssuePointCabinetResponsePayload} from '../../../../shared/models/api/getIssuePointCabinetResponsePayload';
import {AuthorizeEsiaResponsePayload} from '../../../../shared/models/api/authorizeEsiaResponsePayload';
import {CounterpartyLinkedProfile} from '../../../../shared/hooks/useAccountLinkerHook/Types';
import {DeliveryType} from '../../../../types/registerCardApplicationRequestPayload';

interface IssuePointDeliveryInfoProps {
  esiaRegInfo: CounterpartyLinkedProfile | AuthorizeEsiaResponsePayload | null | undefined;
  linkedAccountIdWeb?: number;
  viewVariantForWeb: 'esiaActivateCard' | 'afterLoggedOnMobile';
  issuePointsMap: IssuePointsMapState;
  contentAlign?: 'center' | 'justify';
  canChangeDeliveryMethod: boolean;
}

export function IssuePointDeliveryInfo({
  esiaRegInfo,
  linkedAccountIdWeb,
  viewVariantForWeb,
  issuePointsMap,
  contentAlign = 'justify',
  canChangeDeliveryMethod,
}: IssuePointDeliveryInfoProps) {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const userHasNoCards = useSelector(esiaUserHasNoCardsSelector);

  const styles = useStyles(({mediaOnly, addShadow}) => {
    return {
      tableEsiaActivateCard: {
        display: 'flex',
        padding: 24,
        paddingTop: 0,
        flexDirection: 'row',
        backgroundColor: theme.color.backgroundMenuColor,
        ...mediaOnly(DeviceSize.mobile, {
          flexDirection: 'column',
          maxHeight: '100%',
          paddingBottom: 0,
          paddingTop: 0,
        }),
      },
      tableAfterLogged: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        backgroundColor: theme.color.backgroundMenuColor,
      },
      issuePointInfo: {
        minWidth: 70,
        ...mediaOnly(DeviceSize.desktop, {minHeight: 480}),
        marginRight: 20,
      },
      workingHrsInfo: {
        minWidth: 70,
        ...mediaOnly(DeviceSize.desktop, {minHeight: 480}),
        marginRight: 20,
      },
      workingHrsInfoTitle: {
        marginVertical: 20,
      },
      subtitle: {
        textAlign: contentAlign,
        marginTop: 10,
        marginBottom: 10,
        paddingHorizontal: contentAlign === 'center' ? 20 : 0,
        position: 'relative',
        ...(contentAlign === 'center' ? {width: '100%', alignSelf: 'stretch' as const} : null),
      },
      mapBlock: {
        ...mediaOnly(DeviceSize.desktop, {marginTop: 24}),
        width: '100%',
        borderRadius: 5,
        ...addShadow({height: 2, radius: 18, opacity: 0.1}),
        ...mediaOnly(DeviceSize.mobile, {maxWidth: 400}),
      },
      mapWrapper: {
        height: 300,
        ...mediaOnly(DeviceSize.mobile, {height: 380, paddingHorizontal: 20}),
      },
      row: {
        marginBottom: 20,
      },
      rowWithIcon: {
        flexDirection: 'row',
        maxWidth: '100%',
      },
      icon: {
        marginRight: 12,
        color: theme.color.primaryColor,
        justifyContent: 'center',
        height: 30,
      },
      map: {
        width: '100%',
        height: '100%',
      },
      groupItem: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 12,
        paddingVertical: 5,
      },
      issuePointInfoContainer: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        width: '100%',
      },
      issuePointInfoText: {
        flexShrink: 1,
      },
      firstColumn: {
        marginRight: 20,
        ...mediaOnly(DeviceSize.mobile, {flexDirection: 'column', marginTop: 24}, {width: '50%'}),
      },
      secondColumn: {
        ...mediaOnly(DeviceSize.mobile, {flexDirection: 'column', marginTop: 24}, {width: '50%'}),
      },
      form: {
        marginTop: 20,
        ...mediaOnly(DeviceSize.mobile, {marginTop: 0, marginBottom: 0, alignItems: 'center'}),
      },
    };
  });

  const mapInstance = useRef<any>(null);
  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const {mapData, mapDataRaw, onPressMarker, centerPoint} = issuePointsMap;
  const selectedIssuePoint = useMemo<GetIssuePointCabinetResponsePayload>(
    () => mapDataRaw.find((x) => x.id === esiaRegInfo?.cardDeliveryInfo?.counterpartyDepartmentId)!,
    [mapDataRaw, esiaRegInfo?.cardDeliveryInfo?.counterpartyDepartmentId],
  );

  const defaultMapState = useMemo(() => {
    const geolocation = selectedIssuePoint?.geolocation;
    if (geolocation?.length === 2) {
      return {center: geolocation.flat(), zoom: 15};
    }
    if (centerPoint) {
      return {center: centerPoint, zoom: initialYandexZoomValue};
    }
    return {center: [0, 0] as number[], zoom: initialYandexZoomValue};
  }, [selectedIssuePoint?.id, centerPoint]);

  const applyMapViewport = useCallback(
    (instance: any) => {
      if (!instance || !mapData?.length) {
        return;
      }

      if (userHasNoCards) {
        const targetIssuePoint = mapData.find((x) => x.id === esiaRegInfo?.cardDeliveryInfo?.counterpartyDepartmentId);
        if (targetIssuePoint?.geolocation?.length === 2) {
          onPressMarker(targetIssuePoint);
          instance.setCenter(targetIssuePoint.geolocation);
          instance.setZoom(15);
          return;
        }
      }

      const geolocation = selectedIssuePoint?.geolocation;
      if (geolocation?.length === 2) {
        instance.setCenter(geolocation);
        instance.setZoom(15);
        return;
      }

      if (centerPoint) {
        instance.setCenter(centerPoint);
      }
    },
    [
      mapData,
      userHasNoCards,
      esiaRegInfo?.cardDeliveryInfo?.counterpartyDepartmentId,
      selectedIssuePoint,
      centerPoint,
      onPressMarker,
    ],
  );

  const handleMapInstanceChange = useCallback(
    (instance: any) => {
      mapInstance.current = instance;
      if (instance) {
        applyMapViewport(instance);
      }
    },
    [applyMapViewport],
  );

  useEffect(() => {
    if (mapInstance.current) {
      applyMapViewport(mapInstance.current);
    }
  }, [applyMapViewport]);

  const mapContent = useMemo(() => {
    if (
      linkedAccountIdWeb != null ||
      !selectedIssuePoint ||
      !selectedIssuePoint.geolocation ||
      selectedIssuePoint.geolocation.length <= 1
    ) {
      return null;
    }

    return (
      <YMaps>
        <Map
          instanceRef={handleMapInstanceChange}
          defaultOptions={{autoFitToViewport: 'always'}}
          height='100%'
          width='100%'
          defaultState={{center: defaultMapState.center, zoom: defaultMapState.zoom}}
          onLoad={() => setMapIsLoaded(true)}>
          {mapIsLoaded && (
            <Clusterer defaultOptions={{preset: 'islands#invertedOrangeClusterIcons'}}>
              <Placemark
                key={selectedIssuePoint.id}
                geometry={selectedIssuePoint.geolocation}
                defaultOptions={{
                  iconLayout: 'default#image',
                  iconImageHref: MapPin,
                  iconImageSize: [60, 60],
                  iconImageOffset: [-30, -60],
                }}
              />
            </Clusterer>
          )}
        </Map>
      </YMaps>
    );
  }, [linkedAccountIdWeb, selectedIssuePoint, handleMapInstanceChange, defaultMapState, mapIsLoaded]);

  const mapComponent = mapContent ? <View style={styles.mapWrapper}>{mapContent}</View> : null;

  const webDeliveryInfo = useMemo(() => {
    if (!selectedIssuePoint) {
      return null;
    }

    if (viewVariantForWeb === 'esiaActivateCard') {
      return (
        <View style={styles.mapBlock}>
          <View style={styles.form}>
            <View style={styles.tableEsiaActivateCard}>
              <View style={[styles.firstColumn]}>
                <Text style={[styles.row]} type='h6'>
                  Отделение {selectedIssuePoint.name ?? selectedIssuePoint.organization?.name}
                </Text>
                <View style={[styles.row, styles.rowWithIcon]}>
                  <View style={styles.icon}>
                    <images.Location width={24} height={24} color={theme.color.primaryColor} />
                  </View>
                  <Text style={styles.issuePointInfoText} type='h6Light'>
                    {selectedIssuePoint.address}
                  </Text>
                </View>
                <View style={styles.rowWithIcon}>
                  <View style={styles.icon}>
                    <images.Phone width={24} height={24} color={theme.color.primaryColor} />
                  </View>
                  <Text type='h6Light'>{selectedIssuePoint.phone}</Text>
                </View>
              </View>
              <View style={[styles.secondColumn]}>
                <Text style={[styles.row, styles.rowWithIcon]} type='h6'>
                  Часы работы
                </Text>
                <View style={[styles.row, styles.rowWithIcon]}>
                  <View style={styles.icon}>
                    <images.Clock2 width={24} height={24} color={theme.color.primaryColor} />
                  </View>
                  <Text style={styles.issuePointInfoText} type='h6Light'>
                    {selectedIssuePoint.operatingMode}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          {mapComponent}
        </View>
      );
    }

    return (
      <View style={styles.mapBlock}>
        <View style={styles.tableAfterLogged}>
          <View style={styles.issuePointInfo}>
            <Text style={styles.row} type='h6'>
              Отделение {selectedIssuePoint.name ?? selectedIssuePoint.organization?.name}
            </Text>
            <View style={[styles.row, styles.rowWithIcon]}>
              <View style={styles.icon}>
                <images.Location width={24} height={24} color={theme.color.primaryColor} />
              </View>
              <Text style={styles.issuePointInfoText} type='h6Light'>
                {selectedIssuePoint.address}
              </Text>
            </View>
            <View style={styles.rowWithIcon}>
              <View style={styles.icon}>
                <images.Phone width={24} height={24} color={theme.color.primaryColor} />
              </View>
              <Text type='h6Light'>{selectedIssuePoint.phone}</Text>
            </View>
          </View>
          <View style={styles.workingHrsInfo}>
            <Text style={styles.workingHrsInfoTitle} type='h6'>
              Часы работы
            </Text>
            <View style={styles.rowWithIcon}>
              <View style={styles.icon}>
                <images.Clock2 width={24} height={24} color={theme.color.primaryColor} />
              </View>
              <Text style={styles.issuePointInfoText} type='h6Light'>
                {selectedIssuePoint.operatingMode}
              </Text>
            </View>
          </View>
        </View>
        {mapComponent}
      </View>
    );
  }, [selectedIssuePoint, viewVariantForWeb, styles, theme.color.primaryColor, mapComponent]);

  if (esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId !== DeliveryType.IssuePoint) {
    return null;
  }

  return (
    <>
      <Text type={isDesktop ? 'h3Light' : 'h6Light'} style={styles.subtitle}>
        {canChangeDeliveryMethod
          ? 'После проверки оператором карта будет подготовлена к выдаче выбранным способом. Вы можете изменить способ получения на доставку «Почтой России».'
          : 'Карта готова к выдаче. Обратитесь в пункт выдачи нефинансовой карты. При себе необходимо иметь документы, удостоверяющие личность.'}
      </Text>
      {selectedIssuePoint != null &&
        (Platform.OS === 'web' ? (
          webDeliveryInfo
        ) : (
          <View style={styles.issuePointInfoContainer}>
            <Text style={styles.row} type='h6'>
              Отделение {selectedIssuePoint.name ?? selectedIssuePoint.organization?.name}
            </Text>
            <View style={[styles.row, styles.rowWithIcon]}>
              <View style={styles.icon}>
                <images.Location width={24} height={24} color={theme.color.primaryColor} />
              </View>
              <Text style={styles.issuePointInfoText} type='h6Light'>
                {selectedIssuePoint.address}
              </Text>
            </View>
            <View style={styles.rowWithIcon}>
              <Link
                testID='phone-geo-map-link-id'
                href={`tel:${formatPhoneToCallable(selectedIssuePoint.phone)}`}
                external
                style={styles.groupItem}>
                <View style={styles.icon}>
                  <images.Phone width={24} height={24} color={theme.color.primaryColor} />
                </View>
                <Text style={styles.issuePointInfoText} type='h6Light'>
                  {formatPhone(selectedIssuePoint.phone)}
                </Text>
              </Link>
            </View>
            <Text style={styles.row} type='h6'>
              Часы работы
            </Text>
            <View style={styles.rowWithIcon}>
              <View style={styles.icon}>
                <images.Clock2 width={24} height={24} color={theme.color.primaryColor} />
              </View>
              <Text style={styles.issuePointInfoText} type='h6Light'>
                {selectedIssuePoint.operatingMode}
              </Text>
            </View>
          </View>
        ))}
    </>
  );
}
